import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Briefcase, FileText, FolderGit2, Trophy, BadgeCheck, Youtube,
  AlertCircle, CheckCircle2, X,
} from "lucide-react";
import { Callout, IconButton } from "../ui";
import Shimmer from "../Common/Shimmer";
import NavChip from "./NavChip";
import CareerProfileCard from "./CareerProfileCard";
import ResumeCard from "./ResumeCard";
import ProjectsSection from "./ProjectsSection";
import AchievementsSection from "./AchievementsSection";
import CertificationsSection from "./CertificationsSection";
import VideosSection from "./VideosSection";
import PortfolioStrength from "./PortfolioStrength";
import { fetchMyPortfolio, errorText } from "./portfolioApi";

/**
 * StudentPortfolioPanel — the student's own portfolio, mounted inside Account
 * Settings.
 *
 * ⚠️ ONE FETCH, ONE STATE. Every child returns the whole updated portfolio from
 * its mutation and hands it back through `applyPortfolio`, so nothing refetches
 * and no two sections can disagree about how many projects there are. The one
 * exception is the resume analysis, which returns an analysis rather than a
 * portfolio — `latestAnalysis` is therefore held beside the portfolio rather
 * than inside it.
 *
 * The section rail is a scroll-to rather than a tab set. A portfolio is one
 * document that a student reads top to bottom while filling it in; tabs would
 * hide the empty sections behind a click, which is exactly the wrong default
 * for a screen whose job is to get them filled.
 */

const SECTIONS = [
  { id: "portfolio-profile", label: "Profile", icon: Briefcase },
  { id: "portfolio-resume", label: "Resume", icon: FileText },
  { id: "portfolio-projects", label: "Projects", icon: FolderGit2 },
  { id: "portfolio-achievements", label: "Achievements", icon: Trophy },
  { id: "portfolio-certifications", label: "Certifications", icon: BadgeCheck },
  { id: "portfolio-videos", label: "Videos", icon: Youtube },
];

const StudentPortfolioPanel = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [flash, setFlash] = useState({ tone: "", text: "" });

  useEffect(() => {
    let cancelled = false;
    fetchMyPortfolio()
      .then((res) => { if (!cancelled) setData(res); })
      .catch((err) => { if (!cancelled) setLoadError(errorText(err, "Failed to load your portfolio.")); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  /**
   * The single write path. A null portfolio means "just show this message" —
   * a delete that failed, for instance, where there is nothing new to render.
   */
  const applyPortfolio = useCallback((portfolio, message, tone = "success") => {
    if (portfolio) setData((d) => ({ ...d, portfolio }));
    if (message) setFlash({ tone, text: message });
  }, []);

  const setLatestAnalysis = useCallback((analysis) => {
    setData((d) => ({ ...d, latestAnalysis: analysis }));
  }, []);

  // Auto-dismiss a success note; leave errors on screen until the user clears them.
  useEffect(() => {
    if (flash.tone !== "success" || !flash.text) return undefined;
    const t = setTimeout(() => setFlash({ tone: "", text: "" }), 4000);
    return () => clearTimeout(t);
  }, [flash]);

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (!el) return;
    el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const portfolio = data?.portfolio;
  const counts = useMemo(() => ({
    projects: portfolio?.projects?.length || 0,
    achievements: portfolio?.achievements?.length || 0,
    certifications: portfolio?.certifications?.length || 0,
    videos: portfolio?.showcaseVideos?.length || 0,
  }), [portfolio]);

  if (loading) {
    return (
      <div className="tw-flex tw-flex-col tw-gap-4">
        <Shimmer height={120} />
        <Shimmer height={280} />
        <Shimmer height={220} />
      </div>
    );
  }

  if (loadError) {
    return (
      <Callout tone="danger" role="alert">
        <span className="tw-inline-flex tw-items-center tw-gap-1.5">
          <AlertCircle size={14} /> {loadError}
        </span>
      </Callout>
    );
  }

  return (
    // gap-5 (20px), matching `.acct-panel` in account-settings.scss. Both tabs
    // are the same page, so the rhythm between cards must not change when the
    // reader switches between them. Tailwind's SPACING scale is not remapped in
    // this repo (only borderRadius and letterSpacing are), so gap-5 is 20px.
    <div className="tw-flex tw-flex-col tw-gap-5">
      <PortfolioStrength
        portfolio={portfolio}
        latestAnalysis={data.latestAnalysis}
        earned={data.earned}
        onJump={scrollTo}
      />

      {/* Section rail — sticks under the page header on desktop. */}
      <nav
        aria-label="Portfolio sections"
        className="tw-sticky tw-top-0 tw-z-10 tw--mx-1 tw-flex tw-gap-1 tw-overflow-x-auto tw-bg-surface tw-px-1 tw-py-2"
      >
        {SECTIONS.map(({ id, label, icon: Icon }) => (
          <NavChip key={id} icon={Icon} onClick={() => scrollTo(id)}>
            {label}
          </NavChip>
        ))}
      </nav>

      {flash.text && (
        <Callout tone={flash.tone === "success" ? "success" : "danger"} role="status">
          <span className="tw-flex tw-w-full tw-items-center tw-justify-between tw-gap-2">
            <span className="tw-inline-flex tw-items-center tw-gap-1.5">
              {flash.tone === "success" ? <CheckCircle2 size={14} /> : <AlertCircle size={14} />}
              {flash.text}
            </span>
            <IconButton label="Dismiss" icon={X} size="xs"
              onClick={() => setFlash({ tone: "", text: "" })} />
          </span>
        </Callout>
      )}

      <section id="portfolio-profile" className="tw-scroll-mt-16">
        <CareerProfileCard
          profile={portfolio?.careerProfile}
          onSaved={applyPortfolio}
        />
      </section>

      <section id="portfolio-resume" className="tw-scroll-mt-16">
        <ResumeCard
          resume={portfolio?.resume}
          latestAnalysis={data.latestAnalysis}
          onPortfolioChange={applyPortfolio}
          onAnalysed={setLatestAnalysis}
        />
      </section>

      <section id="portfolio-projects" className="tw-scroll-mt-16">
        <ProjectsSection
          projects={portfolio?.projects}
          onPortfolioChange={applyPortfolio}
        />
      </section>

      <section id="portfolio-achievements" className="tw-scroll-mt-16">
        <AchievementsSection
          achievements={portfolio?.achievements}
          onPortfolioChange={applyPortfolio}
        />
      </section>

      <section id="portfolio-certifications" className="tw-scroll-mt-16">
        <CertificationsSection
          certifications={portfolio?.certifications}
          earned={data.earned}
          onPortfolioChange={applyPortfolio}
        />
      </section>

      <section id="portfolio-videos" className="tw-scroll-mt-16">
        <VideosSection
          videos={portfolio?.showcaseVideos}
          projects={portfolio?.projects || []}
          onPortfolioChange={applyPortfolio}
        />
      </section>

      <p className="tw-m-0 tw-px-1 tw-pb-2 tw-text-xs tw-leading-relaxed tw-text-content-subtle">
        Your portfolio is visible to your institute's administrators and to the instructors of
        the batches you are enrolled in. It is not published publicly.
        {counts.projects + counts.achievements + counts.certifications + counts.videos === 0
          && " Nothing here is required — add what you have."}
      </p>
    </div>
  );
};

export default StudentPortfolioPanel;
